<template>
  <RouterLink
    class="upgrade-promo-banner"
    :class="{ 'upgrade-promo-banner--compact-mobile': compactOnMobile }"
    :to="{ name: RouteName.UPGRADE }"
  >
    <div class="upgrade-promo-banner__content">
      <div class="upgrade-promo-banner__copy">
        <div class="upgrade-promo-banner__eyebrow">
          <ZeroEdgeBadge size="md" />
          <span>{{ t('PROMO_BANNERS.upgrade.eyebrow') }}</span>
        </div>
        <h2>{{ t('PROMO_BANNERS.upgrade.title') }}</h2>
        <p>{{ t('PROMO_BANNERS.upgrade.description') }}</p>
      </div>

      <div class="upgrade-promo-banner__visual" aria-hidden="true">
        <div class="upgrade-promo-banner__ring">
          <span>100%</span>
        </div>
        <div class="upgrade-promo-banner__ticket">
          <i class="fa-solid fa-arrow-trend-up"></i>
        </div>
      </div>

      <span v-if="showCta" class="upgrade-promo-banner__cta">
        {{ t('PROMO_BANNERS.upgrade.cta') }}
        <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
      </span>
    </div>
  </RouterLink>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { RouteName } from '@/router/types'
import ZeroEdgeBadge from '@/components/ZeroEdgeBadge/ZeroEdgeBadge.vue'

withDefaults(
  defineProps<{
    showCta?: boolean
    compactOnMobile?: boolean
  }>(),
  {
    showCta: true,
    compactOnMobile: false,
  },
)

const { t } = useI18n()
</script>

<style scoped lang="scss">
@use 'sass:color';

.upgrade-promo-banner {
  position: relative;
  display: block;
  width: 100%;
  margin: 0 auto 1rem;
  min-height: 132px;
  overflow: hidden;
  border-radius: 12px;
  text-decoration: none;
  color: $text-light-200;
  background:
    radial-gradient(circle at 12% 30%, rgba(55, 241, 133, 0.34), transparent 28%),
    radial-gradient(circle at 78% 45%, rgba(255, 190, 85, 0.32), transparent 30%),
    linear-gradient(120deg, #081b20 0%, #0b2d2a 42%, #36240b 100%);
  border: 1px solid rgba(117, 255, 176, 0.24);
  box-shadow:
    0 18px 34px rgba(0, 0, 0, 0.28),
    0 0 0 1px rgba(255, 213, 128, 0.08) inset;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(100deg, transparent 0%, rgba(255, 255, 255, 0.13) 46%, transparent 58%),
      repeating-linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.035) 0 1px,
        transparent 1px 34px
    );
    background-position:
      64% 0,
      0 0;
    background-size:
      45% 100%,
      100% 100%;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: auto 0 0;
    height: 3px;
    background: linear-gradient(90deg, #37f185, #ffd580, #37f185);
    box-shadow: 0 0 18px rgba(55, 241, 133, 0.6);
  }

  &:hover {
    border-color: rgba(255, 213, 128, 0.58);

    .upgrade-promo-banner__cta {
      border-color: rgba(255, 213, 128, 0.72);
      background: rgba(255, 213, 128, 0.15);
      color: #ffd580;
    }

    .upgrade-promo-banner__ring {
      border-color: rgba(255, 213, 128, 0.82);
    }
  }

  @include mobile {
    min-height: 168px;
    margin-bottom: 0.75rem;
    border-radius: 0;
  }

  :deep(.zero-edge-badge) {
    animation: none;

    &::before {
      display: none;
    }
  }
}

.upgrade-promo-banner__content {
  position: relative;
  z-index: 1;
  min-height: inherit;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem 1.35rem;

  @include tablet {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  @include mobile {
    grid-template-columns: 1fr;
    gap: 0.85rem;
    padding: 1rem;
  }
}

.upgrade-promo-banner__copy {
  min-width: 0;

  h2 {
    margin: 0.35rem 0 0.25rem;
    color: #ffffff;
    font-size: clamp(1.35rem, 2.45vw, 2.25rem);
    font-weight: 950;
    line-height: 0.95;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    text-shadow:
      0 0 18px rgba(55, 241, 133, 0.34),
      0 2px 0 rgba(0, 0, 0, 0.34);
  }

  p {
    margin: 0;
    max-width: 640px;
    color: rgba(231, 247, 239, 0.78);
    font-size: 0.95rem;
    font-weight: 650;
    line-height: 1.45;
  }
}

.upgrade-promo-banner__eyebrow {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: #baffcb;
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.upgrade-promo-banner__visual {
  position: relative;
  display: grid;
  place-items: center;
  width: 180px;
  height: 100px;

  @include tablet {
    width: 140px;
  }

  @include mobile {
    display: none;
  }
}

.upgrade-promo-banner__ring {
  display: grid;
  place-items: center;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 2px solid rgba(117, 255, 176, 0.58);
  background:
    radial-gradient(circle, rgba(55, 241, 133, 0.2), transparent 62%),
    conic-gradient(from -90deg, rgba(55, 241, 133, 0.98), rgba(255, 213, 128, 0.95), rgba(55, 241, 133, 0.98));
  box-shadow:
    0 0 22px rgba(55, 241, 133, 0.28),
    0 0 0 10px rgba(55, 241, 133, 0.06);

  span {
    display: grid;
    place-items: center;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: rgba(6, 16, 22, 0.94);
    color: #ffffff;
    font-size: 1.28rem;
    font-weight: 950;
    box-shadow: inset 0 0 16px rgba(0, 0, 0, 0.48);
  }
}

.upgrade-promo-banner__ticket {
  position: absolute;
  right: 18px;
  top: 12px;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  color: #ffd580;
  background: rgba(255, 213, 128, 0.13);
  border: 1px solid rgba(255, 213, 128, 0.32);
  box-shadow: 0 0 18px rgba(255, 190, 85, 0.24);
  transform: rotate(9deg);

  @include mobile {
    display: none;
  }
}

.upgrade-promo-banner__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 150px;
  padding: 0.72rem 1rem;
  border-radius: 9px;
  border: 1px solid rgba(117, 255, 176, 0.42);
  background: rgba(55, 241, 133, 0.12);
  color: #baffcb;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  @include tablet {
    grid-column: 1 / -1;
    justify-self: start;
  }

  @include mobile {
    width: fit-content;
    min-width: 0;
    padding: 0.62rem 0.82rem;
    justify-self: end;
  }
}

.upgrade-promo-banner--compact-mobile {
  @include mobile {
    min-height: 92px;
    border-radius: $radius-md;

    .upgrade-promo-banner__content {
      grid-template-columns: minmax(0, 1fr) auto;
      gap: $space-sm;
      padding: $space-sm $space-md;
    }

    .upgrade-promo-banner__copy h2 {
      margin-top: $space-2xs;
      font-size: 1.05rem;
      line-height: 1.05;
    }

    .upgrade-promo-banner__copy p {
      display: none;
    }

    .upgrade-promo-banner__eyebrow {
      font-size: 0.62rem;
    }

    .upgrade-promo-banner__cta {
      min-width: auto;
      padding: $space-sm;
      font-size: 0;

      i {
        font-size: 0.9rem;
      }
    }
  }
}

</style>
